import React, { useState } from 'react';
import { Button, Card, Typography, Space, message, Divider, Collapse, Tag, Alert } from 'antd';
import { PlusOutlined, ImportOutlined, EyeOutlined } from '@ant-design/icons';
import { PERMISSION_GROUPS, ROLE_TEMPLATES } from '@configs/rbac-templates';
import {
  apiCreatePermission,
  apiCreateRole,
  apiAssignPermissionsToRole,
  apiGetAllPermissions,
  apiGetAllRoles
} from '@services/ManageRoles/RoleServices';
import { showSuccess, showError } from '@components/notification';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const RBACSeeder = () => {
  const [loading, setLoading] = useState(false);
  const [seedingPermissions, setSeedingPermissions] = useState(false);
  const [seedingRoles, setSeedingRoles] = useState(false);

  const seedPermissions = async () => {
    try {
      setSeedingPermissions(true);
      const allPermissions = Object.values(PERMISSION_GROUPS).flatMap((group) => group.permissions);

      let created = 0;
      let skipped = 0;

      for (const permission of allPermissions) {
        try {
          await apiCreatePermission({
            name: permission.name,
            description: permission.description
          });
          created++;
        } catch (error) {
          // Skip if already exists
          skipped++;
          console.log(`Permission ${permission.name} already exists`);
        }
      }

      showSuccess({
        message: `Đã tạo ${created} permissions mới, bỏ qua ${skipped} permissions đã tồn tại`
      });
    } catch (error) {
      showError(error?.message || 'Không thể khởi tạo permissions');
    } finally {
      setSeedingPermissions(false);
    }
  };

  const seedRoles = async () => {
    try {
      setSeedingRoles(true);

      // First get all existing permissions to map IDs
      const permissionsResponse = await apiGetAllPermissions();
      const existingPermissions = permissionsResponse?.data?.data || [];

      const permissionMap = {};
      existingPermissions.forEach((perm) => {
        permissionMap[perm.name] = perm.id;
      });

      let created = 0;
      let skipped = 0;

      for (const roleTemplate of ROLE_TEMPLATES) {
        try {
          // Create role
          const roleResponse = await apiCreateRole({
            name: roleTemplate.name,
            description: roleTemplate.description
          });

          // Map permission names to IDs for this role
          const allPermissions = Object.values(PERMISSION_GROUPS).flatMap(
            (group) => group.permissions
          );
          const rolePermissionIds = roleTemplate.permissions
            .map((permId) => {
              const permission = allPermissions.find((p) => p.id === permId);
              return permission ? permissionMap[permission.name] : null;
            })
            .filter(Boolean);

          // Assign permissions to role
          if (rolePermissionIds.length > 0) {
            await apiAssignPermissionsToRole({
              role_id: roleResponse.data.data.id,
              permission_ids: rolePermissionIds
            });
          }

          created++;
        } catch (error) {
          // Skip if already exists
          skipped++;
          console.log(`Role ${roleTemplate.name} already exists`);
        }
      }

      showSuccess({
        message: `Đã tạo ${created} roles mới, bỏ qua ${skipped} roles đã tồn tại`
      });
    } catch (error) {
      showError(error?.message || 'Không thể khởi tạo roles');
    } finally {
      setSeedingRoles(false);
    }
  };

  const seedAll = async () => {
    setLoading(true);
    await seedPermissions();
    await seedRoles();
    setLoading(false);
  };

  const getPermissionsByGroup = (permissionIds) => {
    const allPermissions = Object.values(PERMISSION_GROUPS).flatMap((group) => group.permissions);
    return permissionIds.map((id) => allPermissions.find((p) => p.id === id)).filter(Boolean);
  };

  const renderRolePreview = (role) => {
    const permissions = getPermissionsByGroup(role.permissions);
    const groupCounts = {};

    Object.keys(PERMISSION_GROUPS).forEach((groupKey) => {
      const groupPermissions = PERMISSION_GROUPS[groupKey].permissions;
      const hasPermissions = permissions.filter((p) =>
        groupPermissions.some((gp) => gp.id === p.id)
      );
      if (hasPermissions.length > 0) {
        groupCounts[PERMISSION_GROUPS[groupKey].name] = {
          count: hasPermissions.length,
          total: groupPermissions.length
        };
      }
    });

    return (
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          {role.display_name}
        </Title>
        <Paragraph style={{ marginBottom: 12 }}>{role.description}</Paragraph>
        <div>
          <Text strong>Quyền hạn ({permissions.length} quyền):</Text>
          <div style={{ marginTop: 8 }}>
            {Object.entries(groupCounts).map(([groupName, counts]) => (
              <Tag key={groupName} color="blue" style={{ marginBottom: 4 }}>
                {groupName}: {counts.count}/{counts.total}
              </Tag>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3}>Khởi tạo hệ thống phân quyền RBAC</Title>

      <Alert
        message="Lưu ý quan trọng"
        description="Tool này sẽ tạo ra các roles và permissions mẫu dựa trên hệ thống GSWM. Chỉ chạy một lần khi setup hệ thống lần đầu."
        type="warning"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Quick Actions */}
        <Card title="Khởi tạo nhanh">
          <Space>
            <Button
              type="primary"
              icon={<ImportOutlined />}
              onClick={seedAll}
              loading={loading}
              size="large"
              style={{ backgroundColor: '#0284c7', borderColor: '#0284c7' }}>
              Tạo tất cả Roles & Permissions
            </Button>
            <Button icon={<PlusOutlined />} onClick={seedPermissions} loading={seedingPermissions}>
              Chỉ tạo Permissions
            </Button>
            <Button icon={<PlusOutlined />} onClick={seedRoles} loading={seedingRoles}>
              Chỉ tạo Roles
            </Button>
          </Space>
        </Card>

        {/* Permission Groups Preview */}
        <Card title="Nhóm quyền hạn sẽ được tạo">
          <Collapse>
            {Object.entries(PERMISSION_GROUPS).map(([groupKey, group]) => (
              <Panel header={`${group.name} (${group.permissions.length} quyền)`} key={groupKey}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {group.permissions.map((permission) => (
                    <div key={permission.id}>
                      <Text code>{permission.name}</Text>
                      <br />
                      <Text type="secondary">{permission.description}</Text>
                    </div>
                  ))}
                </Space>
              </Panel>
            ))}
          </Collapse>
        </Card>

        {/* Role Templates Preview */}
        <Card title="Vai trò mẫu sẽ được tạo">
          <Collapse>
            {ROLE_TEMPLATES.map((role) => (
              <Panel
                header={`${role.display_name} (${role.permissions.length} quyền)`}
                key={role.name}>
                {renderRolePreview(role)}
              </Panel>
            ))}
          </Collapse>
        </Card>
      </Space>
    </div>
  );
};

export default RBACSeeder;
